'use client';

/**
 * Chat UI for the in-app AI assistant ("Valiyou").
 *
 * Wraps AI SDK useChat against /api/platform/ai/chat. Server tools run on the
 * server; client tools (navigate_to, get_current_page_context) are executed
 * here in the browser and their results are sent back automatically.
 */

import { useRef, useEffect, useState, type FormEvent } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import AiWorkingStar from './AiWorkingStar';
import { BookOpenIcon } from '@heroicons/react/16/solid';
import { HELP_ARTICLES } from '@/lib/platform/ai/help-articles';
import {
  isAiNavigablePath,
  isAiPageAllowed,
  getAiPageLabel,
  getAiPage,
  getAiDocPath,
  type AiGatingFeatures,
} from '@/lib/platform/ai/routes';
import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { useAiAssistant } from '@/lib/platform/contexts/ai-assistant-context';
import {
  useAiFormBridge,
  typewriterFill,
  focusAiField,
} from '@/lib/platform/contexts/ai-form-bridge-context';
import { usePermissionsQuery } from '@/lib/platform/hooks/queries/use-permissions-query';
import { useEntitlements } from '@/lib/platform/hooks/use-entitlements';
import type { PermissionsData } from '@/lib/platform/navigation/get-first-accessible-page';

/** Short status labels shown while a tool call is running. */
const TOOL_LABELS: Record<string, string> = {
  get_context: 'Reading your workspace…',
  list_valuations: 'Looking up valuations…',
  get_valuation: 'Looking up valuation…',
  get_media_entries: 'Looking up media data…',
  list_sponsors: 'Looking up sponsors…',
  get_sponsor: 'Looking up sponsor…',
  list_packages: 'Looking up packages…',
  get_sponsorship_inventory: 'Looking up inventory…',
  list_documents: 'Looking up documents…',
  get_document_link: 'Fetching document link…',
  get_analytics: 'Looking up analytics…',
  navigate_to: 'Opening page…',
  get_current_page_context: 'Reading current page…',
  list_form_fields: 'Reading the form…',
  open_dialog: 'Opening the dialog…',
  fill_form_field: 'Filling in the form…',
  focus_form_field: 'Pointing at the field…',
  get_help_article: 'Reading the Help Center…',
};

/** External links in replies are limited to Valiyou's own domains — always new tab. */
const ALLOWED_EXTERNAL_HOSTS = new Set([
  'valiyou.com',
  'www.valiyou.com',
  'docs.valiyou.com',
  'status.valiyou.com',
  'valiyou.io',
  'www.valiyou.io',
]);

function isAllowedExternalUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && ALLOWED_EXTERNAL_HOSTS.has(parsed.hostname);
  } catch {
    return false;
  }
}

/** Article path → menu title, for the Help Center links. */
const HELP_ARTICLE_TITLES = new Map(HELP_ARTICLES.map((a) => [a.path, a.title]));

/**
 * Help Center title for a docs.valiyou.com URL — null for any other URL.
 * Unknown docs paths fall back to a generic title so the link never shows
 * a long raw URL.
 */
function helpCenterTitle(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' || parsed.hostname !== 'docs.valiyou.com') return null;
    const path = parsed.pathname.replace(/^\/+|\/+$/g, '').replace(/\.md$/, '');
    return HELP_ARTICLE_TITLES.get(path || 'index') ?? 'Help Center';
  } catch {
    return null;
  }
}

/** Bare docs.valiyou.com URLs in reply text (rendered as titled Help Center links). */
const DOCS_URL_PATTERN = /(https:\/\/docs\.valiyou\.com[^\s)\]]*)/g;

/** Starter chips — a chip with a `path` only shows when that page is allowed. */
const SUGGESTIONS: { text: string; path?: string }[] = [
  { text: 'What can you help me with?' },
  { text: 'Show me my latest valuations', path: '/products/valuations/valuations' },
  { text: 'Open the sponsors page', path: '/products/sponsorships/sponsors' },
];

/**
 * Visible text of the current page (<main>), whitespace-collapsed and capped.
 * Sent to the model as ground truth of what the user sees.
 */
const MAX_PAGE_CONTENT_CHARS = 2000;
function readMainContent(): string {
  if (typeof document === 'undefined') return '';
  const text = document.querySelector('main')?.innerText ?? '';
  return text.replace(/\s+/g, ' ').trim().slice(0, MAX_PAGE_CONTENT_CHARS);
}

/** Link handling for in-reply markdown links. */
interface InlineLinkOptions {
  /** True when the path may be shown as a clickable in-app link. */
  isAllowed: (path: string) => boolean;
  /** Navigate the app to the linked page. */
  navigate: (path: string) => void;
}

/**
 * Minimal inline markdown: [label](/path) in-app links + **bold** (model is
 * instructed to keep it light). Links to unknown/gated paths render as text.
 */
function renderInline(text: string, links: InlineLinkOptions): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let key = 0;

  const linkClass =
    'font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300';

  /**
   * Help Center chip: book icon + article title in a pill, opens in a new
   * tab. Font size/weight is inherited from the message text, and the
   * negative vertical margin cancels the pill padding in the line box so
   * lines with a chip keep the same line height as plain text lines.
   */
  const pushHelpLink = (url: string, title: string) => {
    nodes.push(
      <a
        key={key++}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mx-0.5 -my-0.5 inline-flex max-w-full items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-0.5 align-middle text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:hover:bg-indigo-500/20"
      >
        <BookOpenIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate">{title}</span>
      </a>
    );
  };

  const pushBold = (segment: string) => {
    for (const part of segment.split(/(\*\*[^*]+\*\*)/g)) {
      if (part.startsWith('**') && part.endsWith('**')) {
        nodes.push(<strong key={key++}>{part.slice(2, -2)}</strong>);
      } else if (part) {
        nodes.push(<span key={key++}>{part}</span>);
      }
    }
  };

  const pushText = (segment: string) => {
    // Bare docs.valiyou.com URLs become titled Help Center links — raw URLs
    // are too long for the chat column and would break the layout.
    for (const part of segment.split(DOCS_URL_PATTERN)) {
      if (!part) continue;
      if (part.startsWith('https://docs.valiyou.com')) {
        // Trailing sentence punctuation is not part of the URL
        const url = part.replace(/[.,;:!?]+$/, '');
        const title = helpCenterTitle(url);
        if (title) {
          pushHelpLink(url, title);
          if (part.length > url.length) pushBold(part.slice(url.length));
          continue;
        }
      }
      pushBold(part);
    }
  };

  const linkPattern = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = linkPattern.exec(text)) !== null) {
    pushText(text.slice(last, match.index));
    const [, label, path] = match;
    if (path.startsWith('/') && links.isAllowed(path)) {
      nodes.push(
        <button
          key={key++}
          type="button"
          onClick={() => links.navigate(path)}
          className={linkClass}
        >
          {label}
        </button>
      );
    } else if (helpCenterTitle(path)) {
      // Markdown links to the Help Center get the article title as label —
      // the model's own label can be long/raw; the title is always short.
      pushHelpLink(path, helpCenterTitle(path) as string);
    } else if (isAllowedExternalUrl(path)) {
      nodes.push(
        <a key={key++} href={path} target="_blank" rel="noopener noreferrer" className={linkClass}>
          {label}
        </a>
      );
    } else {
      pushText(label);
    }
    last = linkPattern.lastIndex;
  }
  pushText(text.slice(last));
  return nodes;
}

/** Minimal block markdown: paragraphs + "- " bullet lists. */
function renderText(text: string, links: InlineLinkOptions): React.ReactNode {
  const blocks: React.ReactNode[] = [];
  let listItems: string[] = [];
  const lines = text.split('\n');

  const flushList = (key: string) => {
    if (listItems.length > 0) {
      blocks.push(
        <ul key={key} className="my-1 list-disc space-y-0.5 pl-4">
          {listItems.map((item, i) => (
            <li key={i}>{renderInline(item, links)}</li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listItems.push(trimmed.slice(2));
    } else {
      flushList(`list-${i}`);
      if (trimmed) {
        blocks.push(<p key={`p-${i}`}>{renderInline(trimmed, links)}</p>);
      }
    }
  });
  flushList('list-end');
  return <div className="space-y-1.5">{blocks}</div>;
}

/**
 * Current app path including an active `?tab=` when it is a valid AI tab path.
 * usePathname() has no query string, so the tab is read from the location and
 * validated against the allowlist — arbitrary query strings never pass through.
 */
function currentAiPath(pathname: string): string {
  if (typeof window === 'undefined') return pathname;
  const tab = new URLSearchParams(window.location.search).get('tab');
  if (!tab) return pathname;
  const withTab = `${pathname}?tab=${tab}`;
  return isAiNavigablePath(withTab) ? withTab : pathname;
}

export default function AiChat() {
  const router = useRouter();
  const pathname = usePathname();
  const { organization } = useOrganization();
  // Identity-stable context value — safe to hold in the onToolCall closure
  const formBridge = useAiFormBridge();
  const { data: permissions } = usePermissionsQuery();
  const { features } = useEntitlements();
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  // Keep the latest pathname available to the tool handler without re-creating the chat
  const pathnameRef = useRef(pathname);
  // Set on navigation — the next page-context read waits for the render first
  const justNavigatedRef = useRef(false);
  // Latest plan/permission gates for the navigate_to handler (closure-safe)
  const gatesRef = useRef<{ permissions: PermissionsData | undefined; features: AiGatingFeatures }>(
    { permissions, features }
  );
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);
  useEffect(() => {
    gatesRef.current = { permissions, features };
  }, [permissions, features]);

  // Counts in-flight client tool executions (navigation waits, typewriter
  // fills) so the global "AI working" indicator stays on in the gap between
  // stream end and the automatic tool-result resend.
  const [toolActivity, setToolActivity] = useState(0);
  const withToolActivity = async (fn: () => Promise<void>) => {
    setToolActivity((c) => c + 1);
    try {
      await fn();
    } finally {
      setToolActivity((c) => c - 1);
    }
  };

  const { messages, sendMessage, status, error, addToolOutput } = useChat({
    transport: new DefaultChatTransport({ api: '/api/platform/ai/chat' }),
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    onToolCall: ({ toolCall }) =>
      withToolActivity(async () => {
        if (toolCall.dynamic) return;
        if (toolCall.toolName === 'get_current_page_context') {
          // Right after a navigation, give the new page a moment to render
          if (justNavigatedRef.current) {
            justNavigatedRef.current = false;
            await new Promise((resolve) => setTimeout(resolve, 700));
          }
          const current = currentAiPath(pathnameRef.current ?? '');
          addToolOutput({
            tool: 'get_current_page_context',
            toolCallId: toolCall.toolCallId,
            output: {
              pathname: current,
              pageLabel: getAiPageLabel(current) ?? null,
              pageDescription: getAiPage(current)?.description ?? null,
              title: typeof document !== 'undefined' ? document.title : '',
              visibleContent: readMainContent(),
            },
          });
        } else if (toolCall.toolName === 'navigate_to') {
          const { path, confirmDiscard } = toolCall.input as {
            path: string;
            confirmDiscard?: boolean;
          };
          const gates = gatesRef.current;
          if (
            isAiNavigablePath(path) &&
            !isAiPageAllowed(path, gates.permissions, gates.features)
          ) {
            // Plan/permission gate: do NOT navigate (the page would show Access
            // Denied) — turn it into an upsell answer instead.
            addToolOutput({
              tool: 'navigate_to',
              toolCallId: toolCall.toolCallId,
              output: {
                navigated: false,
                error: `This page is not available on the organization's plan or with the user's permissions: ${path}`,
                nextStep:
                  'Explain briefly that this feature is not included in the current plan (or permissions), that it is available on a higher plan, and offer to open the Billing page (/platform/billing) if that page is available.',
              },
            });
          } else if (isAiNavigablePath(path)) {
            // Unsaved-changes guard: never throw away the user's in-progress
            // form input by navigating — require an explicit user confirmation.
            const activeForm = formBridge.getActiveForm();
            if (
              activeForm?.isDirty?.() &&
              path !== currentAiPath(pathnameRef.current ?? '') &&
              !confirmDiscard
            ) {
              addToolOutput({
                tool: 'navigate_to',
                toolCallId: toolCall.toolCallId,
                output: {
                  navigated: false,
                  reason: 'unsaved_changes',
                  error:
                    `The form on the current page ("${activeForm.formId}") has UNSAVED changes — ` +
                    'navigating away would discard them. Do NOT navigate. Ask the user to press ' +
                    "the form's save button first, or to explicitly confirm discarding the " +
                    'changes. Only after an explicit confirmation, call navigate_to again with ' +
                    'confirmDiscard: true.',
                },
              });
              return;
            }
            router.push(path);
            justNavigatedRef.current = true;
            const page = getAiPage(path);
            const docPath = getAiDocPath(path); // tab-specific article when the path has ?tab=
            addToolOutput({
              tool: 'navigate_to',
              toolCallId: toolCall.toolCallId,
              output: {
                navigated: true,
                path,
                pageDescription: page?.description ?? null,
                // Steers the model at decision time (stronger than a prompt rule)
                nextStep:
                  'Now call get_current_page_context to see what the page actually shows, and base your reply on it. If the page is empty, point the user to its create button.' +
                  (docPath
                    ? ` For how-to questions about this page, its Help Center article is '${docPath}' (get_help_article).`
                    : ''),
              },
            });
          } else {
            addToolOutput({
              tool: 'navigate_to',
              toolCallId: toolCall.toolCallId,
              output: { navigated: false, error: `Path not allowed: ${path}` },
            });
          }
        } else if (toolCall.toolName === 'list_form_fields') {
          const form = formBridge.getActiveForm();
          const dialogs = formBridge
            .getDialogs()
            .map((d) => ({ dialogId: d.dialogId, description: d.description }));
          if (!form) {
            addToolOutput({
              tool: 'list_form_fields',
              toolCallId: toolCall.toolCallId,
              output: {
                error:
                  'The current page has no fillable form. Navigate to a form page first (e.g. a valuation edit page).' +
                  (dialogs.length > 0
                    ? ' This page DOES have dialogs you can open with open_dialog — their forms become fillable once open.'
                    : ''),
                dialogs,
              },
            });
          } else {
            addToolOutput({
              tool: 'list_form_fields',
              toolCallId: toolCall.toolCallId,
              output: {
                formId: form.formId,
                description: form.description,
                focusOnly: form.focusOnly === true,
                fields: form.getFields(),
                dialogs,
                nextStep: form.focusOnly
                  ? 'This form is FOCUS-ONLY: use focus_form_field to point at a field — fill_form_field is rejected. Tell the user what to change and which save button to press.'
                  : 'Use fill_form_field with the exact field name. You can only fill — the user presses the save/update button themselves.',
              },
            });
          }
        } else if (toolCall.toolName === 'open_dialog') {
          const { dialogId } = toolCall.input as { dialogId: string };
          const dialog = formBridge.getDialogs().find((d) => d.dialogId === dialogId);
          if (!dialog) {
            const known = formBridge.getDialogs().map((d) => d.dialogId);
            addToolOutput({
              tool: 'open_dialog',
              toolCallId: toolCall.toolCallId,
              output: {
                opened: false,
                error:
                  known.length > 0
                    ? `Unknown dialog: ${dialogId}. Available on this page: ${known.join(', ')}.`
                    : 'The current page has no dialogs the assistant can open.',
              },
            });
            return;
          }
          const opened = dialog.open();
          addToolOutput({
            tool: 'open_dialog',
            toolCallId: toolCall.toolCallId,
            output: opened
              ? {
                  opened: true,
                  dialogId,
                  nextStep:
                    "The dialog is open. Call list_form_fields to see its fields, fill them with fill_form_field, then tell the user to review the values and press the dialog's save button themselves. Never claim anything was saved.",
                }
              : {
                  opened: false,
                  error:
                    'Opening the dialog was blocked (e.g. demo mode). Tell the user why it may be unavailable.',
                },
          });
        } else if (toolCall.toolName === 'fill_form_field') {
          const { name, value } = toolCall.input as { name: string; value: string | number };
          const form = formBridge.getActiveForm();
          if (!form) {
            addToolOutput({
              tool: 'fill_form_field',
              toolCallId: toolCall.toolCallId,
              output: { filled: false, error: 'The current page has no fillable form.' },
            });
            return;
          }
          if (form.focusOnly) {
            addToolOutput({
              tool: 'fill_form_field',
              toolCallId: toolCall.toolCallId,
              output: {
                filled: false,
                error:
                  'This form is read-only for the assistant. Use focus_form_field to point at the field, and tell the user what to change and which save button to press.',
              },
            });
            return;
          }
          const field = form.getFields().find((f) => f.name === name);
          if (!field) {
            addToolOutput({
              tool: 'fill_form_field',
              toolCallId: toolCall.toolCallId,
              output: {
                filled: false,
                error: `Unknown field: ${name}. Call list_form_fields for the exact names.`,
              },
            });
            return;
          }
          // Best effort: scroll to + highlight the field so the user sees the typing
          const focusedBefore = focusAiField(name);
          const filled = await typewriterFill(form, name, value, field.type);
          // New rows (e.g. events[N].name) only exist in the DOM after the fill —
          // retry the scroll/highlight so the user sees where the value landed
          if (filled && !focusedBefore) setTimeout(() => focusAiField(name), 100);
          addToolOutput({
            tool: 'fill_form_field',
            toolCallId: toolCall.toolCallId,
            output: filled
              ? {
                  filled: true,
                  name,
                  value,
                  nextStep:
                    'The field is filled in but NOT saved. Tell the user to review the value and press the save/update button themselves. Never claim it was saved.',
                }
              : { filled: false, error: `Could not fill field: ${name}` },
          });
        } else if (toolCall.toolName === 'focus_form_field') {
          const { name } = toolCall.input as { name: string };
          const form = formBridge.getActiveForm();
          if (!form) {
            addToolOutput({
              tool: 'focus_form_field',
              toolCallId: toolCall.toolCallId,
              output: { focused: false, error: 'The current page has no fillable form.' },
            });
            return;
          }
          const field = form.getFields().find((f) => f.name === name);
          if (!field) {
            addToolOutput({
              tool: 'focus_form_field',
              toolCallId: toolCall.toolCallId,
              output: {
                focused: false,
                error: `Unknown field: ${name}. Call list_form_fields for the exact names.`,
              },
            });
            return;
          }
          const focused = focusAiField(name);
          addToolOutput({
            tool: 'focus_form_field',
            toolCallId: toolCall.toolCallId,
            output: focused
              ? { focused: true, name, label: field.label }
              : {
                  focused: false,
                  error:
                    `The field "${field.label}" is not visible on screen right now — its section ` +
                    'may still be collapsed behind an "+ Add" button. Tell the user where to find it instead.',
                },
          });
        }
      }),
  });

  const isBusy = status === 'submitted' || status === 'streaming';

  // Publish busy state globally (header icon + floating indicator above modals)
  const { setWorking } = useAiAssistant();
  const working = isBusy || toolActivity > 0;
  useEffect(() => {
    setWorking(working);
    return () => setWorking(false);
  }, [working, setWorking]);

  // In-reply links: clickable only for known pages the user may actually open
  const inlineLinks: InlineLinkOptions = {
    isAllowed: (path) => isAiNavigablePath(path) && isAiPageAllowed(path, permissions, features),
    navigate: (path) => router.push(path),
  };

  // Auto-scroll to the latest message
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, status]);

  const submit = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isBusy || !organization?.id) return;
    void sendMessage(
      { text: trimmed },
      // The assistant is locked to the organization the user is currently in.
      // pageContent makes the model content-aware: it sees what the user sees
      // at the moment they write, without needing a page-context tool call.
      {
        body: {
          pathname: currentAiPath(pathnameRef.current ?? ''),
          organizationId: organization.id,
          pageContent: readMainContent(),
        },
      }
    );
    setInput('');
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(input);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Hi! I&apos;m Valiyou AI. Ask me anything.
              <br />I can also fill in forms for you.
            </p>
            <p className="max-w-[260px] text-[11px] leading-snug text-gray-400 dark:text-gray-500">
              When you use this chat, your messages and on-screen data are processed by Anthropic
              (USA).
              <br />
              <a
                href="/trust-center"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-500 dark:hover:text-gray-400"
              >
                Learn more
              </a>
            </p>
            <div className="flex flex-col gap-2">
              {SUGGESTIONS.filter(
                (s) => !s.path || isAiPageAllowed(s.path, permissions, features)
              ).map((suggestion) => (
                <button
                  key={suggestion.text}
                  type="button"
                  onClick={() => submit(suggestion.text)}
                  className="rounded-full border border-gray-200 px-3 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  {suggestion.text}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((message) => (
              <div key={message.id}>
                {message.parts.map((part, index) => {
                  if (part.type === 'text') {
                    return message.role === 'user' ? (
                      <div key={index} className="flex justify-end">
                        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-indigo-600 px-3.5 py-2 text-sm text-white">
                          {part.text}
                        </div>
                      </div>
                    ) : (
                      <div key={index} className="flex justify-start">
                        <div className="max-w-[90%] rounded-2xl rounded-bl-sm bg-gray-100 px-3.5 py-2 text-sm text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                          {renderText(part.text, inlineLinks)}
                        </div>
                      </div>
                    );
                  }
                  // Tool activity: show a muted status line while running
                  if (part.type.startsWith('tool-')) {
                    const toolName = part.type.slice(5);
                    const state = (part as { state?: string }).state;
                    if (state === 'output-available' || state === 'output-error') return null;
                    return (
                      <p
                        key={index}
                        className="px-1 text-xs text-gray-400 italic dark:text-gray-500"
                      >
                        {TOOL_LABELS[toolName] ?? 'Working…'}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>
            ))}
            {status === 'submitted' && (
              <p className="px-1 text-xs text-gray-400 italic dark:text-gray-500">Thinking…</p>
            )}
            {error && (
              <p className="px-1 text-xs text-red-500">
                {error.message.includes('quota_exceeded')
                  ? 'Daily AI message limit reached. Try again tomorrow.'
                  : 'Something went wrong. Please try again in a moment.'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="flex shrink-0 items-end gap-2 border-t border-gray-200 px-3 py-3 dark:border-gray-700"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask Valiyou AI…"
          className="min-w-0 flex-1 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        />
        {/* While the AI works the send button becomes the in-panel activity
            indicator: glowing button + the star sequence (conditional render
            mounts it WITH the animation class so Safari starts the CSS
            animation) */}
        <button
          type="submit"
          disabled={isBusy || !input.trim()}
          aria-label="Send message"
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white transition-colors hover:bg-indigo-500 ${working ? 'ai-working-glow' : 'disabled:opacity-40'}`}
        >
          {working ? (
            <AiWorkingStar className="h-4 w-4" />
          ) : (
            <PaperAirplaneIcon className="h-4 w-4" />
          )}
        </button>
      </form>
    </div>
  );
}
